import { z } from "zod";
import { BilateralPnlCellSchema, BilateralPnlReportSchema, bilateralSourceCells, sumBilateralPnlCells } from "./eex-bilateral-pnl.js";
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const amount = z.number().finite().nullable();
const count = z.number().int().nonnegative();
export const MonthlyPnlMatrixCellSchema = z.object({
    bilateral: BilateralPnlCellSchema.optional(),
    finalEUR: amount,
    estimateEUR: amount,
    finalMissingCount: count,
    estimateMissingCount: count,
    staleCount: count,
    scheduledCount: count,
});
export const MonthlyPnlMatrixSourceTradeSchema = z.object({
    tradeId: z.string(), product: z.string().nullable(), period: z.string().nullable(), date: z.string().nullable(),
});
export const MonthlyPnlMatrixSourceDealSchema = z.object({
    dealId: z.string(), product: z.string().nullable(), period: z.string().nullable(), date: z.string().nullable(),
});
export const MonthlyPnlMatrixGroupBySchema = z.enum(["product", "period", "trader", "book", "kind"]);
const groupingSchema = z.array(MonthlyPnlMatrixGroupBySchema).max(2).refine(values => new Set(values).size === values.length, {
    message: "Choose each grouping dimension once.",
});
export const MonthlyPnlMatrixSchema = z.object({
    schemaVersion: z.literal(1),
    companyId: z.string(),
    generatedAt: z.string(),
    asOf: z.string(),
    months: z.array(month),
    slices: z.array(z.object({
        rowId: z.string(),
        kind: z.enum(["pair", "trade", "deal", "unattributed"]),
        label: z.string(),
        tradeId: z.string().nullable(),
        dealId: z.string().nullable().optional(),
        sourceDeals: z.array(MonthlyPnlMatrixSourceDealSchema).optional(),
        sourceTradeIds: z.array(z.string()),
        tradeDate: z.string().nullable().optional(),
        sourceTradeDates: z.array(z.object({ tradeId: z.string(), date: z.string().nullable() })).optional(),
        sourceTrades: z.array(MonthlyPnlMatrixSourceTradeSchema).optional(),
        book: z.string().nullable(),
        trader: z.string().nullable(),
        cells: z.record(month, MonthlyPnlMatrixCellSchema),
    })),
    warnings: z.array(z.string()),
    bilateralPnl: BilateralPnlReportSchema.optional(),
});
export const MonthlyPnlMatrixFiltersSchema = z.object({
    book: z.string().nullable().optional(),
    trader: z.string().nullable().optional(),
    from: month.optional(),
    to: month.optional(),
    includeEstimates: z.boolean().optional(),
    includeBilateral: z.boolean().optional(),
    search: z.string().max(200).optional(),
    kind: z.enum(["pair", "trade", "deal", "unattributed"]).optional(),
    product: z.string().optional(),
    period: z.string().optional(),
    groupBy: groupingSchema.optional(),
    sortBy: z.union([z.enum(["label", "impact", "total"]), month]).optional(),
    sortDirection: z.enum(["asc", "desc"]).optional(),
}).strict().refine(value => !value.from || !value.to || value.from <= value.to, {
    message: "The first month must be on or before the last month.", path: ["to"],
});
const zeroCell = () => ({
    finalEUR: 0, estimateEUR: 0, finalMissingCount: 0, estimateMissingCount: 0,
    staleCount: 0, scheduledCount: 0,
});
const sumMoney = (left, right) => left === null || right === null ? null : (Math.round(left * 100) + Math.round(right * 100)) / 100;
function addCell(target, cell) {
    if (target.bilateral || cell.bilateral)
        target.bilateral = sumBilateralPnlCells([target.bilateral, cell.bilateral]);
    target.finalEUR = sumMoney(target.finalEUR, cell.finalEUR);
    target.estimateEUR = sumMoney(target.estimateEUR, cell.estimateEUR);
    target.finalMissingCount += cell.finalMissingCount;
    target.estimateMissingCount += cell.estimateMissingCount;
    target.staleCount += cell.staleCount;
    target.scheduledCount += cell.scheduledCount;
}
/** Monetary cells are allocated to cents by Brain before aggregation. */
export function monthlyPnlMatrixAmount(cell, includeEstimates = true) {
    return includeEstimates ? sumMoney(cell.finalEUR, cell.estimateEUR) : cell.finalEUR;
}
/** Execution dates only. Recognition dates and settlement dates are separate. */
export function monthlyPnlMatrixTradeDate(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value))
        return null;
    const day = value.slice(0, 10), parsed = new Date(`${day}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : null;
}
const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const readableDate = (date) => dateFormatter.format(new Date(`${date}T00:00:00Z`));
export function monthlyPnlMatrixTradeDateLabel(row) {
    if (row.kind === "trade" || row.kind === "deal") {
        const date = monthlyPnlMatrixTradeDate(row.tradeDate);
        return date ? `${row.kind === "deal" ? "Contract dated" : "Traded"} ${readableDate(date)}` : row.kind === "deal" ? "Contract date unavailable" : "Trade date unavailable";
    }
    const sources = row.sourceTrades?.length ? row.sourceTrades : row.sourceTradeDates;
    const dates = [...new Set([...sources, ...(row.sourceDeals ?? [])].map(source => monthlyPnlMatrixTradeDate(source.date)).filter((date) => date !== null))].sort();
    if (!dates.length)
        return "Trade dates unavailable";
    const incomplete = [...new Set([...row.sourceTradeIds, ...sources.map(source => source.tradeId)])]
        .some(id => !sources.some(source => source.tradeId === id && monthlyPnlMatrixTradeDate(source.date)));
    return `Traded ${readableDate(dates[0])}${dates.length > 1 ? ` – ${readableDate(dates.at(-1))}` : ""}${incomplete ? " · some dates unavailable" : ""}`;
}
function options(values) {
    return [...new Set(values)].sort((a, b) => a === null ? 1 : b === null ? -1 : a.localeCompare(b));
}
function sourceTradesForSlice(slice) {
    const sources = (slice.sourceTrades ?? []).map(source => ({ ...source,
        product: source.product?.trim() || null, period: source.period?.trim() || null,
        date: monthlyPnlMatrixTradeDate(source.date),
    }));
    for (const tradeId of slice.sourceTradeIds) {
        if (sources.some(source => source.tradeId === tradeId))
            continue;
        sources.push({ tradeId, product: null, period: null,
            date: monthlyPnlMatrixTradeDate(slice.sourceTradeDates?.find(source => source.tradeId === tradeId)?.date ??
                (tradeId === slice.tradeId ? slice.tradeDate : null)) });
    }
    return sources;
}
function uniqueSourceTrades(sources) {
    const unique = new Map();
    for (const source of sources) {
        const key = JSON.stringify([source.tradeId, source.product, source.period]);
        const existing = unique.get(key);
        if (!existing || existing.date === null)
            unique.set(key, { ...source });
    }
    // An older payload may omit metadata on a slice already described by another slice.
    return [...unique.values()].filter(source => source.product !== null || source.period !== null ||
        !sources.some(other => other.tradeId === source.tradeId && (other.product !== null || other.period !== null)))
        .sort((a, b) => a.tradeId.localeCompare(b.tradeId) || (a.product ?? "").localeCompare(b.product ?? "") ||
        (a.period ?? "").localeCompare(b.period ?? ""));
}
const matchesSource = (source, filters) => (filters.product === undefined || source.product === filters.product) &&
    (filters.period === undefined || source.period === filters.period);
/** Filter the effective recognition results; never rerun matching on filtered trades. */
export function projectMonthlyPnlMatrix(data, rawFilters = {}) {
    const filters = MonthlyPnlMatrixFiltersSchema.parse(rawFilters);
    const months = [...new Set(data.months)].sort().filter(value => (!filters.from || value >= filters.from) && (!filters.to || value <= filters.to));
    const grouped = new Map();
    const slices = [...data.slices];
    const financialRowIds = new Set(data.slices.filter(slice => (filters.book === undefined || slice.book === filters.book) &&
        (filters.trader === undefined || slice.trader === filters.trader) &&
        (!filters.kind || slice.kind === filters.kind) && months.some(month => slice.cells[month] !== undefined)).map(slice => slice.rowId));
    for (const source of data.bilateralPnl?.sources ?? []) {
        const isDeal = source.sourceKind === "deal";
        const rowId = JSON.stringify(source.pair ? ["pair", source.pair] : isDeal ? ["deal", source.dealId] : ["trade", source.tradeId]);
        if (!filters.includeBilateral && !financialRowIds.has(rowId))
            continue;
        slices.push({ rowId, kind: source.pair ? "pair" : isDeal ? "deal" : "trade", label: source.pair ?? `${source.product} · ${source.period}`,
            tradeId: source.pair || isDeal ? null : source.tradeId, dealId: isDeal && !source.pair ? source.dealId : null,
            sourceTradeIds: source.tradeId ? [source.tradeId] : [], tradeDate: source.date,
            sourceTrades: source.tradeId ? [{ tradeId: source.tradeId, product: source.product, period: source.period, date: source.date }] : [],
            sourceDeals: isDeal && source.dealId ? [{ dealId: source.dealId, product: source.product, period: source.period, date: source.date }] : [],
            book: source.book, trader: source.trader,
            cells: Object.fromEntries(Object.entries(bilateralSourceCells(source, months)).map(([month, bilateral]) => [month, { ...zeroCell(), bilateral }])) });
    }
    // Classify a PAIR from its complete metadata, before narrowing its numeric ownership or months.
    const metadata = new Map();
    const dealMetadata = new Map();
    for (const slice of slices)
        metadata.set(slice.rowId, [...(metadata.get(slice.rowId) ?? []), ...sourceTradesForSlice(slice)]);
    for (const slice of slices)
        for (const deal of slice.sourceDeals ?? []) {
            const sources = dealMetadata.get(slice.rowId) ?? [];
            if (!sources.some(source => source.dealId === deal.dealId))
                sources.push({ ...deal, date: monthlyPnlMatrixTradeDate(deal.date) });
            dealMetadata.set(slice.rowId, sources);
        }
    for (const [id, sources] of metadata)
        metadata.set(id, uniqueSourceTrades(sources));
    for (const slice of slices) {
        if (filters.book !== undefined && slice.book !== filters.book)
            continue;
        if (filters.trader !== undefined && slice.trader !== filters.trader)
            continue;
        if (filters.kind && slice.kind !== filters.kind)
            continue;
        if (!months.some(value => slice.cells[value] !== undefined))
            continue;
        let row = grouped.get(slice.rowId);
        if (!row) {
            row = { id: slice.rowId, kind: slice.kind, label: slice.label, tradeId: slice.tradeId, dealId: slice.dealId,
                tradeDate: monthlyPnlMatrixTradeDate(slice.tradeDate), sourceTradeDates: [], sourceTradeIds: [],
                sourceTrades: metadata.get(slice.rowId) ?? [], sourceDeals: dealMetadata.get(slice.rowId) ?? [], sourceDealIds: [], products: [], periods: [], isMultiProduct: false,
                books: [], traders: [], cells: {}, total: zeroCell() };
            row.products = options([...row.sourceTrades, ...(row.sourceDeals ?? [])].map(source => source.product)).filter((value) => value !== null);
            row.periods = options([...row.sourceTrades, ...(row.sourceDeals ?? [])].map(source => source.period)).filter((value) => value !== null);
            row.isMultiProduct = row.kind === "pair" && row.products.length > 1;
            for (const value of months)
                row.cells[value] = zeroCell();
            grouped.set(slice.rowId, row);
        }
        row.sourceTradeIds.push(...slice.sourceTradeIds);
        row.sourceDealIds.push(...(slice.sourceDeals ?? []).map(source => source.dealId));
        row.sourceTradeDates.push(...(slice.sourceTradeDates ?? []).map(source => ({ ...source, date: monthlyPnlMatrixTradeDate(source.date) })));
        row.books.push(slice.book);
        row.traders.push(slice.trader);
        for (const value of months) {
            const cell = slice.cells[value];
            if (cell)
                addCell(row.cells[value], cell);
        }
    }
    const search = filters.search?.trim().toLocaleLowerCase();
    // Search whole rows after ownership filtering, retaining every contributing slice.
    const rows = [...grouped.values()].filter(row => ((filters.product === undefined && filters.period === undefined) || [...row.sourceTrades, ...(row.sourceDeals ?? [])].some(source => matchesSource(source, filters))) &&
        (!search || [row.label, row.tradeId, ...row.sourceTradeIds, ...row.sourceTrades.map(source => source.tradeId),
            ...row.books, ...row.traders, ...row.products, ...row.periods, row.tradeDate, row.dealId, ...(row.sourceDeals ?? []).map(source => source.dealId),
            ...(row.tradeDate ? [readableDate(row.tradeDate)] : []),
            ...(row.kind === "pair" ? [...row.sourceTrades, ...(row.sourceDeals ?? [])].flatMap(source => source.date ? [source.date, readableDate(source.date)] : []) : []),
            ...row.sourceTradeDates.flatMap(source => source.date ? [source.date, readableDate(source.date)] : [])]
            .some(value => value?.toLocaleLowerCase().includes(search))));
    const totalsByMonth = {};
    const grandTotal = zeroCell();
    for (const value of months)
        totalsByMonth[value] = zeroCell();
    for (const row of rows) {
        row.sourceTradeIds = [...new Set(row.sourceTradeIds)].sort();
        row.sourceDealIds = [...new Set(row.sourceDealIds)].sort();
        row.sourceTradeDates = row.sourceTradeIds.map(tradeId => ({ tradeId,
            date: row.sourceTradeDates.find(source => source.tradeId === tradeId && source.date !== null)?.date ?? null }));
        row.books = options(row.books);
        row.traders = options(row.traders);
        for (const value of months) {
            addCell(row.total, row.cells[value]);
            addCell(totalsByMonth[value], row.cells[value]);
        }
        addCell(grandTotal, row.total);
    }
    const sortBy = filters.sortBy ?? "label", includeEstimates = filters.includeEstimates ?? true;
    const direction = (filters.sortDirection ?? (sortBy === "label" ? "asc" : "desc")) === "asc" ? 1 : -1;
    rows.sort((a, b) => {
        const tie = a.label.localeCompare(b.label) || a.id.localeCompare(b.id);
        if (sortBy === "label")
            return direction * tie;
        const value = (row) => {
            if (sortBy === "impact") {
                const amounts = months.map(value => monthlyPnlMatrixAmount(row.cells[value], includeEstimates));
                return amounts.some(amount => amount === null) ? null : Math.max(0, ...amounts.map(amount => Math.abs(amount)));
            }
            const cell = sortBy === "total" ? row.total : row.cells[sortBy];
            const amount = cell ? monthlyPnlMatrixAmount(cell, includeEstimates) : null;
            return amount;
        };
        const left = value(a), right = value(b);
        // Unknown valuations remain last in either direction; they are never zero.
        return left === null ? right === null ? tie : 1 : right === null ? -1 : direction * (left - right) || tie;
    });
    return { months, rows, totalsByMonth, grandTotal,
        accountOptions: options(slices.map(slice => slice.book)),
        traderOptions: options(slices.map(slice => slice.trader)),
        productOptions: options([...metadata.values(), ...dealMetadata.values()].flatMap(sources => sources.map(source => source.product))).filter((value) => value !== null),
        periodOptions: options([...metadata.values(), ...dealMetadata.values()].flatMap(sources => sources.map(source => source.period))).filter((value) => value !== null),
        includeEstimates: filters.includeEstimates ?? true };
}
const unknownLabels = { product: "Product unavailable", period: "Delivery period unavailable", trader: "Unassigned trader", book: "Unassigned account", kind: "Unattributed" };
const multipleLabels = { product: "Multiple products", period: "Multiple delivery periods", trader: "Multiple traders", book: "Multiple accounts", kind: "Multiple source types" };
function descriptor(row, dimension, sources = [...row.sourceTrades, ...(row.sourceDeals ?? [])]) {
    const values = options(dimension === "book" ? row.books : dimension === "trader" ? row.traders :
        dimension === "kind" ? [row.kind] : sources.map(source => source[dimension]));
    if (values.length > 1)
        return { dimension, value: `multiple:${dimension}`, label: multipleLabels[dimension] };
    const value = values[0] ?? null;
    const label = value === null ? unknownLabels[dimension] : dimension === "kind" ?
        ({ pair: "PAIRs", trade: "Standalone trades", deal: "Bilateral contracts", unattributed: "Unattributed" })[value] : value;
    return { dimension, value, label };
}
/**
 * Canonical economics occur once. References are deliberately a different type without
 * cells or totals, so neither a table nor an export can mistake a link for another result.
 */
export function groupMonthlyPnlMatrix(projection, rawGroupBy, rawFilters = {}) {
    const groupBy = groupingSchema.parse(rawGroupBy), filters = MonthlyPnlMatrixFiltersSchema.parse(rawFilters);
    const nodes = [];
    const canonicalPaths = new Map();
    const paths = new Map();
    const references = [];
    const pathIds = (path) => path.map((_, index) => `group:${JSON.stringify(path.slice(0, index + 1).map(group => [group.dimension, group.value]))}`);
    for (const row of projection.rows) {
        const multiProduct = row.isMultiProduct && groupBy.includes("product");
        const multiplePeriods = row.kind === "pair" && groupBy.includes("period") &&
            options([...row.sourceTrades, ...(row.sourceDeals ?? [])].map(source => source.period)).length > 1;
        const canonical = multiProduct ? [{ dimension: "multiproduct", value: "multiproduct", label: "Multi-product PAIRs" },
            ...groupBy.filter(dimension => dimension !== "product" && dimension !== "period").map(dimension => descriptor(row, dimension))] :
            groupBy.map(dimension => descriptor(row, dimension));
        paths.set(row.id, canonical);
        canonicalPaths.set(row.id, pathIds(canonical));
        if (!multiProduct && !multiplePeriods)
            continue;
        const byPath = new Map();
        for (const source of [...row.sourceTrades, ...(row.sourceDeals ?? [])].filter(source => matchesSource(source, filters))) {
            const path = groupBy.map(dimension => descriptor(row, dimension, [source]));
            const key = JSON.stringify(path.map(group => [group.dimension, group.value]));
            const existing = byPath.get(key) ?? { path, sources: [], deals: [] };
            if ("dealId" in source)
                existing.deals.push(source);
            else
                existing.sources.push(source);
            byPath.set(key, existing);
        }
        for (const entry of byPath.values())
            references.push({ row, ...entry });
    }
    function insert(path, leaf) {
        let children = nodes;
        const ids = pathIds(path);
        for (let index = 0; index < path.length; index++) {
            const group = path[index], id = ids[index];
            let node = children.find(candidate => candidate.kind === "group" && candidate.id === id);
            if (!node) {
                node = { id, kind: "group", ...group, children: [], sourceCount: 0, referenceCount: 0,
                    cells: Object.fromEntries(projection.months.map(value => [value, zeroCell()])), total: zeroCell() };
                children.push(node);
            }
            if (node.kind !== "group")
                throw new Error("Expected a matrix group.");
            node.sourceCount += leaf.sourceCount;
            node.referenceCount += leaf.referenceCount;
            if (leaf.kind === "source") {
                for (const value of projection.months)
                    addCell(node.cells[value], leaf.cells[value]);
                addCell(node.total, leaf.total);
            }
            children = node.children;
        }
        children.push(leaf);
    }
    for (const row of projection.rows)
        insert(paths.get(row.id), {
            id: `source:${row.id}`, kind: "source", label: row.label, children: [], row, targetRowId: row.id,
            canonicalPath: canonicalPaths.get(row.id), cells: row.cells, total: row.total, sourceCount: 1, referenceCount: 0,
        });
    for (const { row, path, sources, deals } of references)
        insert(path, {
            id: `reference:${JSON.stringify([row.id, path.map(group => [group.dimension, group.value])])}`,
            kind: "reference", label: row.label, children: [], targetRowId: row.id,
            canonicalPath: canonicalPaths.get(row.id), product: options([...sources, ...deals].map(source => source.product)).length === 1 ? [...sources, ...deals][0].product : null,
            period: options([...sources, ...deals].map(source => source.period)).length === 1 ? [...sources, ...deals][0].period : null,
            sourceTrades: sources, sourceDeals: deals, sourceCount: 0, referenceCount: 1,
        });
    const sortBy = filters.sortBy ?? "label";
    const direction = (filters.sortDirection ?? (sortBy === "label" ? "asc" : "desc")) === "asc" ? 1 : -1;
    const sortedValue = (node) => {
        if (node.kind === "reference" || node.sourceCount === 0)
            return null;
        if (sortBy === "impact") {
            const amounts = projection.months.map(value => monthlyPnlMatrixAmount(node.cells[value], projection.includeEstimates));
            return amounts.some(amount => amount === null) ? null : Math.max(0, ...amounts.map(amount => Math.abs(amount)));
        }
        const cell = sortBy === "total" ? node.total : node.cells[sortBy];
        return cell ? monthlyPnlMatrixAmount(cell, projection.includeEstimates) : null;
    };
    function sortNodes(items) {
        items.sort((a, b) => {
            const special = Number(b.kind === "group" && b.dimension === "multiproduct") - Number(a.kind === "group" && a.dimension === "multiproduct");
            if (special)
                return special;
            const links = Number(a.kind === "reference") - Number(b.kind === "reference");
            if (links)
                return links;
            const tie = a.label.localeCompare(b.label) || a.id.localeCompare(b.id);
            if (sortBy === "label")
                return direction * tie;
            const left = sortedValue(a), right = sortedValue(b);
            return left === null ? right === null ? tie : 1 : right === null ? -1 : direction * (left - right) || tie;
        });
        for (const node of items)
            if (node.kind === "group")
                sortNodes(node.children);
    }
    // With no grouping, preserve the already sorted source projection exactly.
    if (groupBy.length)
        sortNodes(nodes);
    const canonicalRows = [];
    const collect = (items) => {
        for (const item of items) {
            if (item.kind === "source")
                canonicalRows.push(item.row);
            else if (item.kind === "group")
                collect(item.children);
        }
    };
    collect(nodes);
    return { nodes, canonicalRows, months: projection.months, totalsByMonth: projection.totalsByMonth,
        grandTotal: projection.grandTotal, includeEstimates: projection.includeEstimates };
}
export function buildMonthlyPnlMatrixQuery(filters = {}) {
    const value = MonthlyPnlMatrixFiltersSchema.parse(filters);
    const query = new URLSearchParams();
    if (value.book !== undefined)
        query.set("book", JSON.stringify(value.book));
    if (value.trader !== undefined)
        query.set("trader", JSON.stringify(value.trader));
    if (value.from)
        query.set("from", value.from);
    if (value.to)
        query.set("to", value.to);
    if (value.includeBilateral !== undefined)
        query.set("includeBilateral", String(value.includeBilateral));
    if (value.includeEstimates !== undefined)
        query.set("includeEstimates", String(value.includeEstimates));
    if (value.search)
        query.set("search", value.search);
    if (value.kind)
        query.set("kind", value.kind);
    if (value.product !== undefined)
        query.set("product", value.product);
    if (value.period !== undefined)
        query.set("period", value.period);
    if (value.groupBy !== undefined)
        query.set("groupBy", value.groupBy.join(","));
    if (value.sortBy)
        query.set("sortBy", value.sortBy);
    if (value.sortDirection)
        query.set("sortDirection", value.sortDirection);
    return query;
}
const identityQuery = z.string().transform((value, ctx) => {
    try {
        const parsed = JSON.parse(value);
        if (parsed === null || typeof parsed === "string")
            return parsed;
    }
    catch { /* Validation issue below also covers invalid JSON. */ }
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Expected a JSON string or null." });
    return z.NEVER;
});
const querySchema = z.object({
    book: identityQuery.optional(), trader: identityQuery.optional(),
    from: month.optional(), to: month.optional(),
    includeBilateral: z.enum(["true", "false"]).transform(value => value === "true").optional(),
    includeEstimates: z.enum(["true", "false"]).transform(value => value === "true").optional(),
    search: z.string().max(200).optional(), kind: z.enum(["pair", "trade", "deal", "unattributed"]).optional(),
    product: z.string().optional(), period: z.string().optional(),
    groupBy: z.string().transform(value => value === "" ? [] : value.split(",")).pipe(groupingSchema).optional(),
    sortBy: z.union([z.enum(["label", "impact", "total"]), month]).optional(), sortDirection: z.enum(["asc", "desc"]).optional(),
}).strict();
export function parseMonthlyPnlMatrixQuery(query) {
    return MonthlyPnlMatrixFiltersSchema.parse(querySchema.parse(query));
}
//# sourceMappingURL=eex-pnl-matrix.js.map